<template>
  <HrPageLayout
    title="Disciplinary Cases"
    subtitle="Report incidents, schedule hearings, record sanctions, and close cases"
    icon="bx bx-gavel"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Disciplinary' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-primary" @click="openCreate">
        <i class="bx bx-plus"></i> Open Case
      </button>
    </template>

    <div class="hr-metrics-grid hr-mb-4">
      <div class="hr-metric-card gradient-1">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Total</p>
        <h2 class="hr-metric-value">{{ metrics.total || 0 }}</h2>
            </div>
      </div>
      <div class="hr-metric-card gradient-4">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Open</p>
        <h2 class="hr-metric-value">{{ metrics.open_count || 0 }}</h2>
            </div>
      </div>
      <div class="hr-metric-card gradient-3">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Hearings</p>
        <h2 class="hr-metric-value">{{ metrics.hearing_count || 0 }}</h2>
            </div>
      </div>
      <div class="hr-metric-card gradient-2">
        <div class="hr-metric-content">
              <p class="hr-metric-label">Closed</p>
        <h2 class="hr-metric-value">{{ metrics.closed_count || 0 }}</h2>
            </div>
      </div>
    </div>

    <div class="hr-chart-card hr-mb-4">
      <div class="hr-chart-body hr-py-3">
        <div class="mod-filters">
          <select v-model="filterStatus" class="hr-select" @change="loadCases">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="under_review">Under review</option>
            <option value="hearing_scheduled">Hearing scheduled</option>
            <option value="sanction_imposed">Sanction imposed</option>
            <option value="closed">Closed</option>
            <option value="dismissed">Dismissed</option>
          </select>
          <input v-model="search" type="search" class="hr-input" placeholder="Search case or employee..." @input="debouncedLoad" />
        </div>
      </div>
    </div>

    <div v-if="loading" class="hr-loading-wrap"><div class="hr-spinner"></div></div>
    <div v-else-if="!cases.length" class="hr-empty-state">
      <i class="bx bx-folder-open"></i>
      <p>No disciplinary cases yet.</p>
    </div>
    <div v-else class="hr-table-wrapper">
      <table class="hr-table hr-table-hover">
        <thead>
          <tr>
            <th>Case #</th>
            <th>Employee</th>
            <th>Title</th>
            <th>Category</th>
            <th>Status</th>
            <th class="hr-text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in cases" :key="c.id">
            <td><code>{{ c.case_number }}</code></td>
            <td>{{ c.employee_name }}<br><span class="hr-text-muted hr-small">{{ c.employee_emp_no }}</span></td>
            <td>{{ c.title }}</td>
            <td>{{ c.category_name }}</td>
            <td><span class="hr-badge" :class="statusClass(c.status)">{{ labelStatus(c.status) }}</span></td>
            <td class="hr-text-end">
              <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" @click="openDetail(c)">Manage</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Create -->
    <div v-if="showCreate" class="hr-modal-overlay" @click.self="showCreate = false">
      <div class="hr-modal-dialog hr-modal-lg">
        <div class="hr-modal-content">
          <div class="hr-modal-header">
            <h5>Open disciplinary case</h5>
            <button type="button" class="hr-modal-close" @click="showCreate = false"><i class="bx bx-x"></i></button>
          </div>
          <div class="hr-modal-body">
            <div class="hr-form-grid hr-form-grid-2">
              <div class="hr-field-full">
                <label class="hr-form-label">Employee *</label>
                <input v-model="empSearch" class="hr-input" placeholder="Search employee..." @input="searchEmployees" />
                <div v-if="empResults.length" class="mod-search-results">
                  <button v-for="e in empResults" :key="e.id" type="button" class="mod-search-item" @click="pickEmployee(e)">
                    {{ e.fullName }} — {{ e.empNo }}
                  </button>
                </div>
                <p v-if="createForm.employeeName" class="hr-text-success hr-small hr-mt-1">Selected: {{ createForm.employeeName }}</p>
              </div>
              <div>
                <label class="hr-form-label">Category *</label>
                <select v-model="createForm.categoryId" class="hr-select">
                  <option value="">Select...</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </div>
              <div>
                <label class="hr-form-label">Incident date</label>
                <input v-model="createForm.incidentDate" type="date" class="hr-input" />
              </div>
              <div class="hr-field-full">
                <label class="hr-form-label">Title *</label>
                <input v-model="createForm.title" class="hr-input" />
              </div>
              <div class="hr-field-full">
                <label class="hr-form-label">Description *</label>
                <textarea v-model="createForm.description" class="hr-input" rows="4"></textarea>
              </div>
            </div>
          </div>
          <div class="hr-modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showCreate = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="submitCreate">{{ saving ? 'Saving...' : 'Open case' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail -->
    <div v-if="showDetail" class="hr-modal-overlay" @click.self="closeDetail">
      <div class="hr-modal-dialog hr-modal-xl">
        <div class="hr-modal-content" v-if="detail">
          <div class="hr-modal-header">
            <h5>{{ detail.case.case_number }} — {{ detail.case.title }}</h5>
            <button type="button" class="hr-modal-close" @click="closeDetail"><i class="bx bx-x"></i></button>
          </div>
          <div class="hr-modal-body">
            <div class="mod-flow">
              <span v-for="step in flowSteps" :key="step.key" class="mod-flow-step" :class="{ active: step.key === detail.case.status, done: stepDone(step.key) }">{{ step.label }}</span>
            </div>
            <p class="hr-mb-3"><strong>{{ detail.case.employee_name }}</strong> · {{ detail.case.category_name }}</p>
            <p class="hr-text-muted">{{ detail.case.description }}</p>

            <div class="mod-section">
              <h6>Witnesses</h6>
              <ul v-if="detail.witnesses.length"><li v-for="w in detail.witnesses" :key="w.id">{{ w.name }} — {{ w.statement || 'No statement' }}</li></ul>
              <p v-else class="hr-text-muted hr-small">No witnesses recorded.</p>
              <div class="hr-form-grid hr-form-grid-2 hr-mt-2">
                <input v-model="witnessForm.name" class="hr-input" placeholder="Witness name" />
                <input v-model="witnessForm.contact" class="hr-input" placeholder="Contact" />
                <textarea v-model="witnessForm.statement" class="hr-input hr-field-full" rows="2" placeholder="Statement"></textarea>
              </div>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary hr-mt-2" @click="addWitness">Add witness</button>
            </div>

            <div class="mod-section">
              <h6>Hearings</h6>
              <ul v-if="detail.hearings.length">
                <li v-for="h in detail.hearings" :key="h.id">{{ formatDt(h.scheduled_at) }} @ {{ h.location || 'TBD' }} ({{ h.status }})</li>
              </ul>
              <div class="hr-form-grid hr-form-grid-2 hr-mt-2">
                <input v-model="hearingForm.scheduledAt" type="datetime-local" class="hr-input" />
                <input v-model="hearingForm.location" class="hr-input" placeholder="Location" />
              </div>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary hr-mt-2" @click="scheduleHearing">Schedule hearing</button>
            </div>

            <div class="mod-section">
              <h6>Sanctions</h6>
              <ul v-if="detail.sanctions.length">
                <li v-for="s in detail.sanctions" :key="s.id"><strong>{{ s.sanction_type }}</strong>: {{ s.description }}</li>
              </ul>
              <div class="hr-form-grid hr-form-grid-2 hr-mt-2">
                <input v-model="sanctionForm.sanctionType" class="hr-input" placeholder="Sanction type" />
                <input v-model="sanctionForm.startDate" type="date" class="hr-input" />
                <textarea v-model="sanctionForm.description" class="hr-input hr-field-full" rows="2" placeholder="Description"></textarea>
              </div>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-warning hr-mt-2" @click="imposeSanction">Record sanction</button>
            </div>

            <div class="mod-section" v-if="!isTerminal(detail.case.status)">
              <button type="button" class="hr-btn hr-btn-sm hr-btn-outline" @click="setStatus('under_review')">Mark under review</button>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-success hr-ml-2" @click="closeCase('closed')">Close case</button>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary hr-ml-2" @click="closeCase('dismissed')">Dismiss</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, toast } from "@/api";

const FLOW = [
  { key: 'open', label: 'Open' },
  { key: 'under_review', label: 'Review' },
  { key: 'hearing_scheduled', label: 'Hearing' },
  { key: 'sanction_imposed', label: 'Sanction' },
  { key: 'closed', label: 'Closed' },
];

export default {
  name: "DisciplinaryManagement",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      cases: [],
      metrics: {},
      categories: [],
      filterStatus: "",
      search: "",
      debounceTimer: null,
      showCreate: false,
      showDetail: false,
      detail: null,
      empSearch: "",
      empResults: [],
      createForm: { employeeId: "", employeeName: "", categoryId: "", title: "", description: "", incidentDate: "" },
      witnessForm: { name: "", contact: "", statement: "" },
      hearingForm: { scheduledAt: "", location: "" },
      sanctionForm: { sanctionType: "", description: "", startDate: "" },
      flowSteps: FLOW,
    };
  },
  mounted() {
    this.loadCategories();
    this.loadCases();
  },
  methods: {
    debouncedLoad() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.loadCases(), 350);
    },
    async loadCategories() {
      const d = new FormData();
      d.append("function", "getDisciplinaryCategories");
      const res = await execute(d, authUrl);
      if (res?.data?.success) this.categories = res.data.data?.categories || [];
    },
    async loadCases() {
      this.loading = true;
      const d = new FormData();
      d.append("function", "getDisciplinaryCases");
      if (this.filterStatus) d.append("status", this.filterStatus);
      if (this.search) d.append("search", this.search);
      try {
        const res = await execute(d, authUrl);
        if (res?.data?.success) {
          this.cases = res.data.data?.cases || [];
          this.metrics = res.data.data?.metrics || {};
        }
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.createForm = { employeeId: "", employeeName: "", categoryId: "", title: "", description: "", incidentDate: "" };
      this.empSearch = "";
      this.empResults = [];
      this.showCreate = true;
    },
    async searchEmployees() {
      const d = new FormData();
      d.append("function", "searchEmployeesForHrModule");
      d.append("q", this.empSearch);
      const res = await execute(d, authUrl);
      this.empResults = res?.data?.data?.employees || [];
    },
    pickEmployee(e) {
      this.createForm.employeeId = e.id;
      this.createForm.employeeName = e.fullName;
      this.empResults = [];
      this.empSearch = e.fullName;
    },
    async submitCreate() {
      if (!this.createForm.employeeId || !this.createForm.categoryId || !this.createForm.title || !this.createForm.description) {
        toast.error("Fill all required fields");
        return;
      }
      this.saving = true;
      const d = new FormData();
      d.append("function", "createDisciplinaryCase");
      Object.entries(this.createForm).forEach(([k, v]) => { if (k !== "employeeName" && v) d.append(k, v); });
      try {
        const res = await execute(d, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Case opened");
          this.showCreate = false;
          this.loadCases();
        } else toast.error(res?.data?.message || "Failed");
      } finally {
        this.saving = false;
      }
    },
    async openDetail(c) {
      const d = new FormData();
      d.append("function", "getDisciplinaryCaseDetail");
      d.append("id", c.id);
      const res = await execute(d, authUrl);
      if (res?.data?.success) {
        this.detail = res.data.data;
        this.showDetail = true;
      }
    },
    closeDetail() {
      this.showDetail = false;
      this.detail = null;
      this.loadCases();
    },
    async post(fn, fields) {
      const d = new FormData();
      d.append("function", fn);
      Object.entries(fields).forEach(([k, v]) => { if (v != null && v !== "") d.append(k, v); });
      const res = await execute(d, authUrl);
      if (res?.data?.success) {
        toast.success(res.data.message || "Saved");
        await this.openDetail({ id: this.detail.case.id });
        return true;
      }
      toast.error(res?.data?.message || "Failed");
      return false;
    },
    addWitness() {
      this.post("addDisciplinaryWitness", { caseId: this.detail.case.id, ...this.witnessForm });
      this.witnessForm = { name: "", contact: "", statement: "" };
    },
    scheduleHearing() {
      this.post("scheduleDisciplinaryHearing", { caseId: this.detail.case.id, ...this.hearingForm });
    },
    imposeSanction() {
      this.post("imposeDisciplinarySanction", { caseId: this.detail.case.id, ...this.sanctionForm });
    },
    setStatus(status) {
      this.post("updateDisciplinaryCaseStatus", { id: this.detail.case.id, status });
    },
    closeCase(status) {
      this.post("updateDisciplinaryCaseStatus", { id: this.detail.case.id, status, resolutionNotes: "Case resolved" });
    },
    statusClass(s) {
      const m = { open: "hr-badge-warning", hearing_scheduled: "hr-badge-info", sanction_imposed: "hr-badge-danger", closed: "hr-badge-success", dismissed: "hr-badge-secondary" };
      return m[s] || "hr-badge-secondary";
    },
    labelStatus(s) {
      return String(s || "").replace(/_/g, " ");
    },
    formatDt(v) {
      if (!v) return "—";
      return new Date(v).toLocaleString();
    },
    isTerminal(s) {
      return s === "closed" || s === "dismissed";
    },
    stepDone(key) {
      const order = FLOW.map((f) => f.key);
      const cur = order.indexOf(this.detail?.case?.status);
      const idx = order.indexOf(key);
      return idx >= 0 && cur > idx;
    },
  },
};
</script>

<style scoped>
.mod-filters { display: flex; gap: 12px; flex-wrap: wrap; }
.mod-filters .hr-select, .mod-filters .hr-input { min-width: 200px; }
.mod-search-results { border: 1px solid #e2e8f0; border-radius: 8px; max-height: 160px; overflow: auto; margin-top: 4px; }
.mod-search-item { display: block; width: 100%; text-align: left; padding: 8px 12px; border: none; background: #fff; cursor: pointer; }
.mod-search-item:hover { background: #f1f5f9; }
.mod-flow { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.mod-flow-step { padding: 4px 10px; border-radius: 999px; font-size: 12px; background: #e2e8f0; color: #64748b; }
.mod-flow-step.active { background: #4f46e5; color: #fff; }
.mod-flow-step.done { background: #22c55e; color: #fff; }
.mod-section { margin-top: 20px; padding-top: 16px; border-top: 1px solid #e2e8f0; }
.hr-modal-xl { max-width: 900px; width: 95%; }
</style>
